import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const STANDARD_BADGES = [
  { label: 'HIPAA', desc: 'PHI Protection · Audit Trails · Access Controls', color: '#94335a', icon: '🔐' },
  { label: 'GDPR', desc: 'Article 5/6/17 · Consent Management · Right to Erasure', color: '#10b981', icon: '🇪🇺' },
  { label: 'ISO 27001', desc: 'Security Events · Incident Management · ISMS', color: '#8b5cf6', icon: '🛡️' },
  { label: 'Kenya DPA 2019', desc: 'Local Data Protection · Privacy by Design', color: '#f59e0b', icon: '🇰🇪' },
  { label: 'HL7 FHIR R4', desc: 'FHIR Resource IDs · Interoperability', color: '#06b6d4', icon: '⚕️' },
  { label: 'ICD-10/11', desc: 'Diagnosis Coding · Clinical Classification', color: '#ec4899', icon: '📋' },
];

const ACTION_COLOR = {
  READ_PHI: '#f59e0b', WRITE: '#94335a', LOGIN: '#10b981',
  DELETE: '#ef4444', UPDATE: '#8b5cf6', READ: '#a99aa0',
  CREATE: '#06b6d4',
};

export default function Compliance() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterParty, setFilterParty] = useState('');
  const limit = 25;

  const fetchLogs = () => {
    setLoading(true);
    const params = { skip: page * limit, limit };
    if (filterAction) params.action = filterAction;
    if (filterParty) params.party_id = filterParty;
    api.get('/audit-logs', { params })
      .then(r => {
        setLogs(r.data?.data || []);
        setTotal(r.data?.meta?.total || 0);
      })
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchLogs(); }, [page, filterAction, filterParty]);

  const actions = ['', 'READ_PHI', 'READ', 'WRITE', 'CREATE', 'UPDATE', 'DELETE', 'LOGIN'];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#2b1b22', margin: 0 }}>Compliance & Audit</h1>
        <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>
          HIPAA · GDPR · ISO 27001 · Kenya DPA — immutable audit trail
        </p>
      </div>

      {/* Standards grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {STANDARD_BADGES.map(({ label, desc, color, icon }) => (
          <div key={label} style={{
            background: '#fff', borderRadius: 10, padding: '14px 18px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
            borderLeft: `4px solid ${color}`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span>{icon}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#2b1b22' }}>{label}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#d1fae5', padding: '2px 8px', borderRadius: 10 }}>Active</span>
            </div>
            <div style={{ fontSize: 11, color: '#8b7681' }}>{desc}</div>
          </div>
        ))}
      </div>

      {/* Audit log */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#2b1b22' }}>Audit Log</h3>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#8b7681' }}>
              Append-only · {total.toLocaleString()} total entries
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <select
              value={filterAction}
              onChange={e => { setFilterAction(e.target.value); setPage(0); }}
              style={{ padding: '7px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 12 }}
            >
              {actions.map(a => <option key={a} value={a}>{a || 'All Actions'}</option>)}
            </select>
            <input
              value={filterParty}
              onChange={e => { setFilterParty(e.target.value); setPage(0); }}
              placeholder="Filter by Party ID..."
              style={{ padding: '7px 12px', border: '1px solid #ece3e7', borderRadius: 8, fontSize: 12, width: 200 }}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ color: '#a99aa0', padding: 20, fontSize: 13 }}>Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div style={{ color: '#a99aa0', textAlign: 'center', padding: 40, fontSize: 13 }}>
            No audit logs match your filters
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
                    {['Timestamp', 'Party ID', 'Action', 'Resource', 'Method', 'Path', 'IP', 'Status'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#8b7681', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f8fafc', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '8px 12px', color: '#5e4a53', whiteSpace: 'nowrap' }}>
                        {log.created_at ? new Date(log.created_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', color: '#8b7681', fontFamily: 'monospace', fontSize: 11 }}>
                        {log.party_name || (log.party_id ? log.party_id.slice(0, 12) + '...' : 'anon')}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                          background: `${ACTION_COLOR[log.action] || '#a99aa0'}20`,
                          color: ACTION_COLOR[log.action] || '#a99aa0',
                        }}>
                          {log.action || 'READ'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#5e4a53' }}>{log.resource_type || '—'}</td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                          background: log.http_method === 'GET' ? '#fdeef5' : '#faf5ff',
                          color: log.http_method === 'GET' ? '#0369a1' : '#7e22ce',
                        }}>
                          {log.http_method || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 12px', color: '#8b7681', fontFamily: 'monospace', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.resource_path || log.path || '—'}
                      </td>
                      <td style={{ padding: '8px 12px', color: '#a99aa0', fontFamily: 'monospace', fontSize: 11 }}>
                        {log.ip_address || '—'}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        <span style={{
                          padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                          background: (log.status_code >= 200 && log.status_code < 300) ? '#d1fae5' : '#fee2e2',
                          color: (log.status_code >= 200 && log.status_code < 300) ? '#065f46' : '#b91c1c',
                        }}>
                          {log.status_code || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
              <div style={{ fontSize: 12, color: '#8b7681' }}>
                Showing {page * limit + 1}–{Math.min((page + 1) * limit, total)} of {total}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)} style={{
                  padding: '6px 14px', background: page === 0 ? '#f8fafc' : '#fff',
                  border: '1px solid #ece3e7', borderRadius: 6, fontSize: 12,
                  cursor: page === 0 ? 'not-allowed' : 'pointer', color: page === 0 ? '#a99aa0' : '#5e4a53',
                }}>← Prev</button>
                <button disabled={(page + 1) * limit >= total} onClick={() => setPage(p => p + 1)} style={{
                  padding: '6px 14px', background: (page + 1) * limit >= total ? '#f8fafc' : '#94335a',
                  border: '1px solid #ece3e7', borderRadius: 6, fontSize: 12,
                  cursor: (page + 1) * limit >= total ? 'not-allowed' : 'pointer',
                  color: (page + 1) * limit >= total ? '#a99aa0' : '#fff',
                }}>Next →</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
